import React from 'react';
import PdfHeader from './PdfHeader';
import PdfFooter from './PdfFooter';
import PdfInfoCard from './PdfInfoCard';
import { compileAvoidedEmissionsByDrivetrainData, compileAvoidedEmissionsViewGraphData, compileDistanceBreakdownByAltDrivetrainData, compileDistanceBreakdownByDrivetrainData, compileFleetCompositionCardData, compileFleetDistanceBreakdownData, compileGeneratedEmissionsByDrivetrainData, compileGeneratedEmissionsViewGraphData, compileHomeViewGraphData, formatData, getEmissionsUnits, getFuelUnits, noDataMessageText, pageHasData, formatTimestamp, formatLongDate } from './UtilityFunctions';
import { Page, Text, View, Document, StyleSheet, Link } from '@react-pdf/renderer';
import { altDrivetrains, combinedEmissionsBars, fleetCondensedDrivetrains, fleetDrivetrains } from './HelperConstants';
import PdfGraphCard from './PdfGraphCard';
import { pageStyle } from '../../styles/emit-styles/PdfStylesheet';
import PdfDistributionCard from './PdfDistributionCard';
import { blue_green, darkest_gray, dark_gray } from '../../styles/emit-styles/ColorScheme';
import { DateTime } from "luxon";
import { formatNumber } from './UtilityFunctions';

const styles = StyleSheet.create({
  page: pageStyle,
  distributionCardColumn: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 16
  },
  distributionCardRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  sectionHeader: {
    fontSize: 14,
    color: darkest_gray,
    fontWeight: 500
  },
  sectionBody: {
    fontSize: 12,
    color: dark_gray,
    lineHeight: 1.2
  },
  sectionBodySpaceAbove: {
    fontSize: 12,
    color: dark_gray,
    lineHeight: 1.2,
    marginTop: 4
  },
  filterSection: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 16
  },
  filterColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    width: 300
  },
  filterLabel: {
    fontSize: 12,
    color: darkest_gray
  },
  bold: {
    fontWeight: 600
  },
  italic: {
    fontStyle: 'italic'
  },
  graphAndChartContainer: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    width: '100%'
  },
  infoCardContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16
  },
  infoCardRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
    width: '100%'
  },
  infoCardRowForTwo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 26,
    width: '100%'
  },
  customLink: {
    color: blue_green
  },
  aboutText: {
    fontSize: 12,
    color: dark_gray,
    marginVertical: 4
  },
  section: {
    marginBottom: 16
  },
  infoAndDistributionCardContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  }
});

export default function PdfReport({ summaryData, graphData, selectedVehicleClasses, vehicleClasses, selectedGroups, viewDataByMonth, isTd, fleetHasAltFuels, methodology, dbDisplayName, macroScale, metricUnitPrefs, userSettings, beginDate, endDate}) {
  if (summaryData && graphData) {
    const FINE_PM = formatNumber(2.5, userSettings);

    const hasReductionData = summaryData.ghgAvoided !== 0;

    const combinedEmissionsData = compileHomeViewGraphData(graphData, viewDataByMonth, false, false);
    const fleetCompositionCardData = compileFleetCompositionCardData(summaryData);
    const fleetDistanceBreakdownData = compileFleetDistanceBreakdownData(summaryData);

    const generatedEmissionsData = compileGeneratedEmissionsViewGraphData(graphData, viewDataByMonth, false, false);
    const generatedEmissionsByDrivetrainData = compileGeneratedEmissionsByDrivetrainData(summaryData);
    const distanceBreakdownByDrivetrainData = compileDistanceBreakdownByDrivetrainData(summaryData);

    const avoidedEmissionsGraphData = compileAvoidedEmissionsViewGraphData(graphData, viewDataByMonth, false, false);
    const avoidedEmissionsByDrivetrainData = compileAvoidedEmissionsByDrivetrainData(summaryData);
    const distanceBreakdownByAltDrivetrainData = compileDistanceBreakdownByAltDrivetrainData(summaryData);

    const selectedClassesText = () => {
      if (selectedVehicleClasses.length === vehicleClasses.length) {
        return 'All Classes';
      } else {
        return selectedVehicleClasses.join(', ');
      }
    }

    const aboutMethodologyCopy = (methodology) => {
      if (methodology.value === 'moves3') {
        return `The emissions calculations for this product are calculated for carbon monoxide (CO), carbon dioxide (CO₂), nitrous oxide (NOₓ), particulate matter ${FINE_PM}µ (PM ${FINE_PM}µ), and particulate matter 10µ (PM 10µ) based on coefficients publicly available from the EPA's MOVES3 model and the Argonne National Laboratory’s AFLEET tool.`;
      } else {
        return "The emissions calculations for this product are calculated for carbon dioxide (CO₂), methane (CH₄), nitrous oxide (NOₓ), and biofuel CO₂ (BioCO₂) based on coefficients publicly available through the Greenhouse Gas Protocol."
      }
    }

    const infoCardHeight = 80;
    const graphCardHeight = 300;
    const infoCardMinWidth = 145;
    const infoCardMaxWidth = 160;
    // Need to hardcode these due to the way the react-pdf-charts library interfaces with recharts (we can't use the react-pdf styling like 'auto' or '100%', the SVG conversion requires a numeric value)
    const graphCardWidth = 562;
    const detailDistributionCardWidth = 273;
    const summaryDistributionCardWidth = 240;

    const graphUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false);
    
    let reportDate = formatTimestamp(userSettings, DateTime.local());
    let groupList = selectedGroups.map((g) => g.name)


    // TODO: Break this massive component down into page-specific components, just to make it more readable. It's a behemoth right now. -NK
    return (
      <Document>
        <Page size='LETTER' orientation='portrait' style={styles.page}>
          <PdfHeader dbDisplayName={dbDisplayName} />
          <View id='Summary'>
            <View style={styles.section}>
              <Text style={styles.sectionBody}>
                This report summarizes your fleet emissions from {formatLongDate(beginDate, userSettings)} to {formatLongDate(endDate, userSettings)}. Emit tracks your emissions based on your daily driving and the fuel type for each vehicle. For additional information on your emissions by vehicle, please go to your <Link style={styles.customLink} src='https://dashboard.sawatchlabs.com/'>Sawatch Labs dashboard</Link> to view details in Emit.
              </Text>
            </View>
            <View style={styles.filterSection}>
              <View style={styles.filterColumn}>
                <Text style={styles.filterLabel}><Text style={styles.bold}>Report Date:</Text> {reportDate.date}</Text>
                {/* TODO: Once methodology functionality exists, update this value so it reflects the current setting */}
                <Text style={styles.filterLabel}><Text style={styles.bold}>Methodology:</Text> {methodology.label}</Text>
              </View>
              <View style={styles.filterColumn}>
                <Text style={styles.filterLabel}><Text style={styles.bold}>Groups:</Text> {groupList.join(', ')}</Text>
                <Text style={styles.filterLabel}><Text style={styles.bold}>Vehicle Classes:</Text> {selectedClassesText()}</Text>
              </View>
            </View>
            {pageHasData('home', summaryData)
              ?
              <>
                <View style={styles.graphAndChartContainer}>
                  <PdfGraphCard
                    height={graphCardHeight}
                    width={graphCardWidth}
                    title='Monthly Emissions (Cumulative)'
                    data={combinedEmissionsData}
                    barInfo={combinedEmissionsBars}
                    yAxisLabel={`GHG (${graphUnits})`}
                    xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                    hasReductionData={hasReductionData}
                    // including these settings here as a possible nice-to-have for the future
                    // if we want add in additional functionality for the PDF graph to respect the current product settings, additional work would be needed,
                    // but this is one less step to take to make that happen.
                    viewDataByMonth={viewDataByMonth}
                    showDiscreteData={false}
                    splitByDrivetrain={false}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    combinedView
                    userSettings={userSettings}
                  />
                  <View style={styles.infoAndDistributionCardContainer}>
                    <View style={styles.infoCardContainer}>
                      <View style={styles.infoCardRow}>
                        <PdfInfoCard
                          value={formatData('ghg', summaryData.ghgGenerated, userSettings)}
                          caption='Generated GHG Emissions'
                          label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                          height={infoCardHeight}
                          width={infoCardMinWidth}
                        />
                        <PdfInfoCard
                          value={formatData('ghg', summaryData.ghgAvoided, userSettings, fleetHasAltFuels)}
                          caption='Avoided GHG Emissions'
                          label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                          height={infoCardHeight}
                          width={infoCardMinWidth}
                        />
                      </View>
                      <View style={styles.infoCardRow}>
                        <PdfInfoCard
                          value={formatData('fuelCostSaved', summaryData.fuelCostSaved, userSettings, fleetHasAltFuels)}
                          caption='Total Fuel Savings'
                          height={infoCardHeight}
                          width={infoCardMinWidth}
                        />
                        <PdfInfoCard
                          value={formatData('fuelUnitsSaved', summaryData.fuelUnitsSaved, userSettings, fleetHasAltFuels)}
                          caption='Total Fuel Reduction'
                          label={getFuelUnits(metricUnitPrefs.volume, true)}
                          height={infoCardHeight}
                          width={infoCardMinWidth}
                        />
                      </View>
                    </View>
                    <View style={styles.distributionCardColumn}>
                      <PdfDistributionCard
                        title='Fleet Composition'
                        subtitle='All Vehicles'
                        height={infoCardHeight}
                        width={summaryDistributionCardWidth}
                        dataTypes={fleetDrivetrains}
                        data={fleetCompositionCardData}
                        dataType='allVehicles'
                      />
                      <PdfDistributionCard
                        title='Distance Breakdown'
                        subtitle='By Fuel Type'
                        height={infoCardHeight}
                        width={summaryDistributionCardWidth}
                        dataTypes={fleetCondensedDrivetrains}
                        data={fleetDistanceBreakdownData}
                        metricUnitPrefs={metricUnitPrefs}
                        isTd={isTd}
                        dataType='simplifiedDistance'
                        userSettings={userSettings}
                      />
                    </View>
                  </View>
                </View>
              </>
              :
              <Text style={styles.sectionBodySpaceAbove}>{noDataMessageText('home', 'pdfReport')}</Text>
              }
          </View>
          <View id='Generated Emissions' break>
            <Text style={styles.sectionHeader}>Generated Emissions</Text>
            {pageHasData('generated-emissions', summaryData)
              ?
              <>
                <View style={styles.section}></View>
                <View style={styles.graphAndChartContainer}>
                  <PdfGraphCard
                    height={graphCardHeight}
                    width={graphCardWidth}
                    title='Monthly Emissions (Cumulative)'
                    data={generatedEmissionsData}
                    barInfo={combinedEmissionsBars}
                    yAxisLabel={`GHG (${graphUnits})`}
                    xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                    viewDataByMonth={true}
                    showDiscreteData={false}
                    splitByDrivetrain={false}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    userSettings={userSettings}
                  />
                  <View style={styles.distributionCardRow}>
                    <PdfDistributionCard
                      title='Distance Breakdown'
                      subtitle='By Fuel Type'
                      height={infoCardHeight}
                      width={detailDistributionCardWidth}
                      dataTypes={fleetDrivetrains}
                      data={distanceBreakdownByDrivetrainData}
                      dataType='allDistance'
                      metricUnitPrefs={metricUnitPrefs}
                      isTd={isTd}
                      userSettings={userSettings}
                    />
                    <PdfDistributionCard
                      title='Cumulative Emissions'
                      subtitle='By Fuel Type'
                      height={infoCardHeight}
                      width={detailDistributionCardWidth}
                      dataTypes={fleetDrivetrains}
                      data={generatedEmissionsByDrivetrainData}
                      dataType='generatedEmissions'
                      metricUnitPrefs={metricUnitPrefs}
                      macroScale={macroScale}
                      userSettings={userSettings}
                    />
                  </View>
                </View>
                {methodology.value === 'moves3'
                  ?
                  <View style={styles.infoCardContainer}>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('ghg', summaryData.ghgGenerated, userSettings)}
                        caption='GHG Emissions'
                        label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Including GHG from kWh gen.)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co2', summaryData.co2Generated, userSettings)}
                        caption='Carbon Dioxide (CO₂)'
                        label={getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co', summaryData.coGenerated, userSettings)}
                        caption='Carbon Monoxide (CO)'
                        label={getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('nox', summaryData.noxGenerated, userSettings)}
                        caption='Nitrous Oxide (NOₓ)'
                        label={getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('pm10', summaryData.pm10Generated, userSettings)}
                        caption={`Particulate Matter 10\u00B5m (PM 10\u00B5m)`}
                        label={getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('pm25', summaryData.pm25Generated, userSettings)}
                        caption={`Particulate Matter ${FINE_PM}\u00B5m (PM ${FINE_PM}\u00B5m)`}
                        label={getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                  </View>
                  :
                  <View style={styles.infoCardContainer}>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('ghg', summaryData.ghgGenerated, userSettings)}
                        caption='GHG Emissions'
                        label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Including GHG from kWh gen.)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co2', summaryData.co2Generated, userSettings)}
                        caption='Carbon Dioxide (CO₂)'
                        label={getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('ch4', summaryData.ch4Generated, userSettings)}
                        caption='Methane (CH₄)'
                        label={getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                    <View style={styles.infoCardRowForTwo}>
                      <PdfInfoCard
                        value={formatData('nox', summaryData.noxGenerated, userSettings)}
                        caption='Nitrous Oxide (NOₓ)'
                        label={getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('bioco2', summaryData.bioco2Generated, userSettings)}
                        caption='Biofuel CO₂ (BioCO₂)'
                        label={getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                  </View>
                }
              </>
              :
              <View style={styles.section}>
                <Text style={styles.sectionBodySpaceAbove}>{noDataMessageText('generated-emissions', 'pdfReport')}</Text>
              </View>
            }
          </View>
          <View id='Avoided Emissions' break>
            <Text style={styles.sectionHeader}>Avoided Emissions</Text>

            {pageHasData('avoided-emissions',summaryData)
              ?
              <>
                <View style={styles.section}></View>
                <View style={styles.graphAndChartContainer}>
                  <PdfGraphCard
                    height={graphCardHeight}
                    width={graphCardWidth}
                    title='Monthly Avoided Emissions (Cumulative)'
                    data={avoidedEmissionsGraphData}
                    barInfo={combinedEmissionsBars}
                    yAxisLabel={`GHG (${graphUnits})`}
                    xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                    viewDataByMonth={true}
                    showDiscreteData={false}
                    splitByDrivetrain={false}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    userSettings={userSettings}
                  />
                  <View style={styles.distributionCardRow}>
                    <PdfDistributionCard
                      title='Distance Breakdown'
                      subtitle='By Fuel Type'
                      height={infoCardHeight}
                      width={detailDistributionCardWidth}
                      dataTypes={altDrivetrains}
                      data={distanceBreakdownByAltDrivetrainData}
                      dataType='altFuelDistance'
                      isTd={isTd}
                      metricUnitPrefs={metricUnitPrefs}
                      userSettings={userSettings}
                    />
                    <PdfDistributionCard
                      title='Avoided Emissions'
                      subtitle='By Fuel Type'
                      height={infoCardHeight}
                      width={detailDistributionCardWidth}
                      dataTypes={altDrivetrains}
                      data={avoidedEmissionsByDrivetrainData}
                      dataType='avoidedEmissions'
                      metricUnitPrefs={metricUnitPrefs}
                      macroScale={macroScale}
                      userSettings={userSettings}
                    />
                  </View>
                </View>
                {methodology.value === 'moves3'
                  ?
                  <View style={styles.infoCardContainer}>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('ghg', summaryData.ghgAvoided, userSettings)}
                        caption='Avoided GHG Emissions'
                        label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Including GHG from kWh gen.)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co2', summaryData.co2Avoided, userSettings)}
                        caption='Carbon Dioxide (CO₂)'
                        label={getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co', summaryData.coAvoided, userSettings)}
                        caption='Carbon Monoxide (CO)'
                        label={getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('nox', summaryData.noxAvoided, userSettings)}
                        caption='Nitrous Oxide (NOₓ)'
                        label={getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('pm10', summaryData.pm10Avoided, userSettings)}
                        caption={`Particulate Matter 10\u00B5m (PM 10\u00B5m)`}
                        label={getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('pm25', summaryData.pm25Avoided, userSettings)}
                        caption={`Particulate Matter ${FINE_PM}\u00B5m (PM ${FINE_PM}\u00B5m)`}
                        label={getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                  </View>
                  :
                  <View style={styles.infoCardContainer}>
                    <View style={styles.infoCardRow}>
                      <PdfInfoCard
                        value={formatData('ghg', summaryData.ghgAvoided, userSettings)}
                        caption='Avoided GHG Emissions'
                        label={getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Including GHG from kWh gen.)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('co2', summaryData.co2Avoided, userSettings)}
                        caption='Carbon Dioxide (CO₂)'
                        label={getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('ch4', summaryData.ch4Avoided, userSettings)}
                        caption='Methane (CH₄)'
                        label={getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                    <View style={styles.infoCardRowForTwo}>
                      <PdfInfoCard
                        value={formatData('nox', summaryData.noxAvoided, userSettings)}
                        caption='Nitrous Oxide (NOₓ)'
                        label={getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                      <PdfInfoCard
                        value={formatData('bioco2', summaryData.bioco2Avoided, userSettings)}
                        caption='Biofuel CO₂ (BioCO₂)'
                        label={getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, true)}
                        subcaption='(Tailpipe)'
                        height={infoCardHeight}
                        minWidth={infoCardMinWidth}
                        maxWidth={infoCardMaxWidth}
                        width={'100%'}
                      />
                    </View>
                  </View>
                }
              </>
              :
              <View style={styles.section}>
                <Text style={styles.sectionBodySpaceAbove}>{noDataMessageText('avoided-emissions', 'pdfReport', fleetHasAltFuels)}</Text>
              </View>
            }
          </View>
          <View id='About' break>
            <View style={styles.section}>
              <Text style={styles.sectionHeader}>Emissions Reporting Data Source - {isTd ? "Telematics Data" : "Fuel Transaction Data"}</Text>
              <Text style={styles.aboutText}>
                {isTd ?
                  "Your fleet telematics data was analyzed to generate the emissions for each vehicle."
                  :
                  "Your fleet fuel card transaction data was analyzed to generate the emissions for each vehicle."
                }
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.sectionHeader}>Emissions Report Inputs</Text>
              <Text style={styles.aboutText}><Text style={styles.italic}>Selected Methodology:</Text> {methodology.label}</Text>
              <Text style={styles.aboutText}>{aboutMethodologyCopy(methodology)}</Text>
              <Text style={styles.aboutText}>The coefficients are then adjusted to reflect the age and type of the vehicle, producing a value in grams per mile which are then recomposed into grams per gallon of fuel.</Text>
              <Text style={styles.aboutText}>Results are available for electric, gasoline, diesel and CNG-powered vehicles.</Text>
              <Text style={styles.aboutText}>For further questions, please contact Sawatch Labs at <Link style={styles.customLink} src={'mailto:info@sawatchlabs.com'}>info@sawatchlabs.com</Link>.</Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.sectionHeader}>Sources:</Text>
              {methodology.value === 'moves3'
                ?
                <Text style={styles.aboutText}><Link style={styles.customLink} src='https://nepis.epa.gov/Exe/ZyPDF.cgi?Dockey=P100U8YT.pdf'>Greenhouse Gas Emissions from a Typical Passenger Vehicle</Link></Text>
                :
                <Text style={styles.aboutText}><Link style={styles.customLink} src='https://ghgprotocol.org/'>Greenhouse Gas Protocol</Link></Text>
              }
              <Text style={styles.aboutText}><Link style={styles.customLink} src='https://www.fueleconomy.gov/feg/pdfs/420r16010.pdf'>Light-Duty Automotive Technology, Carbon Dioxide Emissions, and Fuel Economy Trends</Link></Text>
              <Text style={styles.aboutText}><Link style={styles.customLink} src='https://greet.es.anl.gov/afleet_tool'>Argonne National Laboratory AFLEET Tool</Link></Text>
            </View>
          </View>
          <PdfFooter />
        </Page>
      </Document >
    );
  }
  else return <Document />; // prevent an error in the console on first load - the usePDF hook isn't happy with a null input. The user will never have access to an empty document regardless.
}